// __EQUIP_COMPAT__ (2026-10-04) The weights / deck / features part of the Truck and
// Trailer forms. Which fields show depends on the type's class (tractor / open deck /
// enclosed / pickup / bulk - utils/equipmentCompat.js). Values are in the viewer's
// units (Settings > units): lb + ft, or kg + m. utils/equipmentForm.js converts.
import React from 'react';
import { typeInfo, classFields, EQUIPMENT_CLASSES, EQUIPMENT_FEATURES, TRAILER_ONLY_FEATURES } from '../utils/equipmentCompat';
import { unitLabels } from '../utils/equipmentForm';

export default function EquipmentSpecFields({ kind, type, spec, setSpec, units, inputStyle, labelStyle, gvwrRequired }) {
  const info = typeInfo(kind, type);
  const show = classFields(info.cls);
  const u = unitLabels(units);
  const set = (k) => (e) => setSpec({ ...spec, [k]: e.target.value });
  const num = (k, label, hint, required) => (
    <div>
      <label style={labelStyle}>{label} ({k === 'gvwr' || k === 'tare' || k === 'payload' || k === 'gcwr' ? u.weight : u.length}){required ? ' *' : ''}</label>
      <input type="number" min="0" step="any" value={spec[k]} onChange={set(k)} style={inputStyle} required={!!required} />
      {hint && <small style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{hint}</small>}
    </div>
  );
  const toggle = (f) => {
    const has = (spec.features || []).includes(f);
    setSpec({ ...spec, features: has ? spec.features.filter((x) => x !== f) : [...(spec.features || []), f] });
  };
  const h4 = { color: 'var(--accent-primary)', margin: 0, fontSize: '1rem' };
  return (
    <>
      <div style={{ gridColumn: '1 / -1', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
        {info.label}: <strong>{EQUIPMENT_CLASSES[info.cls]}</strong>.{' '}
        {info.cls === 'tractor' && (kind === 'truck' ? 'Carries nothing on its own - pair it with a trailer in Combinations. Its GCWR and tare limit what the pair can carry.' : '')}
        {info.cls === 'open' && 'Job length/width are checked against the deck; travel height = deck height + cargo height, checked against the legal height in Settings > Equipment rules.'}
        {info.cls === 'enclosed' && 'Job length/width/height are checked against the inside of the box.'}
        {info.cls === 'pickup' && 'Job length/width are checked against the box.'}
        {info.cls === 'bulk' && 'Loose / liquid loads: only weight is checked (cargo rules decide what may go in it).'}
      </div>

      <div style={{ gridColumn: '1 / -1', marginTop: '0.75rem' }}><h4 style={h4}>Weights</h4></div>
      {num('gvwr', 'GVWR - max gross weight', kind === 'truck' ? 'Truck + its own load, as rated' : 'Trailer + its load, as rated', gvwrRequired)}
      {num('tare', 'Tare (empty) weight', 'Needed for payload = GVWR - tare' + (kind === 'truck' && info.cls === 'tractor' ? ' and GCWR - tares' : ''))}
      {info.cls !== 'tractor' && num('payload', 'Payload capacity', 'What may be loaded on it. Blank = GVWR - tare')}
      {kind === 'truck' && show.gcwr && num('gcwr', 'GCWR - combination rating', 'Tractor + trailer + load, as rated')}

      {show.deck && (
        <>
          <div style={{ gridColumn: '1 / -1', marginTop: '0.75rem' }}><h4 style={h4}>{info.cls === 'enclosed' ? 'Inside dimensions' : info.cls === 'pickup' ? 'Box' : 'Deck'}</h4></div>
          {num('deckLength', info.cls === 'enclosed' ? 'Inside length' : 'Usable deck length')}
          {num('deckWidth', info.cls === 'enclosed' ? 'Inside width' : 'Deck width')}
          {show.height && num('insideHeight', 'Inside height')}
          {show.deckHeight && num('deckHeight', 'Deck height from ground', 'Lowest point the cargo sits on')}
        </>
      )}

      {/* __EQUIP_R5__ own half-load caps - Henry: "half-load affects each unit differently (per-axle and total vehicle caps)" */}
      {(
        <div style={{ gridColumn: '1 / -1', marginTop: '0.75rem' }}>
          <h4 style={{ ...h4, marginBottom: '0.3rem' }}>Half-load limits (optional)</h4>
          <small style={{ color: 'var(--text-muted)', fontSize: '0.72rem', display: 'block', marginBottom: '0.4rem' }}>
            What this {kind} may weigh WITH its load during the half-load season (dates in Settings &gt; Equipment rules): a total and / or a
            cap per axle or axle group. The smaller of the total and the axle caps added up counts; minus the tare = cargo allowed.
            Blank = the fleet-wide half-load rule (if one is set), else no half-load limit for this unit.
          </small>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end' }}>
            <div>
              <label style={labelStyle}>Total ({u.weight})</label>
              <input type="number" min="0" step="any" value={spec.halfLoadTotal || ''} onChange={set('halfLoadTotal')} style={{ ...inputStyle, width: '130px' }} />
            </div>
            {(spec.halfLoadAxles || []).map((a, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.25rem', alignItems: 'flex-end' }}>
                <div>
                  <label style={labelStyle}>Axle / group</label>
                  <input value={a.axle} placeholder={i === 0 ? 'steer' : 'drive tandem'} style={{ ...inputStyle, width: '120px' }}
                    onChange={(e) => setSpec({ ...spec, halfLoadAxles: spec.halfLoadAxles.map((x, j) => (j === i ? { ...x, axle: e.target.value } : x)) })} />
                </div>
                <div>
                  <label style={labelStyle}>Cap ({u.weight})</label>
                  <input type="number" min="0" step="any" value={a.cap} style={{ ...inputStyle, width: '110px' }}
                    onChange={(e) => setSpec({ ...spec, halfLoadAxles: spec.halfLoadAxles.map((x, j) => (j === i ? { ...x, cap: e.target.value } : x)) })} />
                </div>
                <button type="button" title="Remove" onClick={() => setSpec({ ...spec, halfLoadAxles: spec.halfLoadAxles.filter((_, j) => j !== i) })}
                  style={{ padding: '0.35rem 0.5rem', background: 'transparent', color: 'var(--color-danger)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer' }}>&times;</button>
              </div>
            ))}
            <button type="button" onClick={() => setSpec({ ...spec, halfLoadAxles: [...(spec.halfLoadAxles || []), { axle: '', cap: '' }] })}
              style={{ padding: '0.4rem 0.7rem', background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer' }}>+ Axle cap</button>
          </div>
        </div>
      )}

      {show.features && (
        <div style={{ gridColumn: '1 / -1', marginTop: '0.75rem' }}>
          <h4 style={{ ...h4, marginBottom: '0.4rem' }}>Features</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem' }}>
            {Object.entries(EQUIPMENT_FEATURES).filter(([k]) => kind === 'trailer' || !TRAILER_ONLY_FEATURES.includes(k)).map(([k, label]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={(spec.features || []).includes(k)} onChange={() => toggle(k)} />
                {label}
              </label>
            ))}
          </div>
          <small style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>Cargo rules use these, e.g. machinery needs ramps, a tilt deck or a beavertail.</small>
        </div>
      )}
    </>
  );
}
